import { types } from "@vilocnv/allsetra-core";
import { mapPreferencesRepository } from "app/data/repositories/mapPreferences.repository";
import { MapPreferencesCache } from "app/data/utils/MapPreferencesCache";
import { resolveMapPreferences } from "app/data/helpers/mapPreferences.helpers";
import { MapViewPreferences } from "app/data/types/mapPreferences.types";
import {
  selectDashboardUserEmail,
  selectDrawerSelectedAccountId
} from "app/data/selectors";
import { useAppSelector } from "hooks/reduxHooks";
import { useCallback } from "react";

export const hasActiveMapFilters = (filters: types.IMapFilter): boolean =>
  (filters.groups?.length ?? 0) > 0 ||
  (filters.objectTypes?.length ?? 0) > 0 ||
  (filters.keyId?.length ?? 0) > 0;

const useMapPreferencesPersistence = () => {
  const accountId = useAppSelector(selectDrawerSelectedAccountId);
  const userEmail = useAppSelector(selectDashboardUserEmail);
  const userId = userEmail ?? undefined;

  const canPersist = Boolean(userId && accountId);

  const load = useCallback(async (): Promise<MapViewPreferences | null> => {
    if (!userId || !accountId) return null;
    return mapPreferencesRepository.load(userId, accountId);
  }, [userId, accountId]);

  const save = useCallback(
    async (preferences: MapViewPreferences): Promise<void> => {
      if (!userId || !accountId) return;
      await mapPreferencesRepository.save(userId, accountId, preferences);
    },
    [userId, accountId]
  );

  /** Sync local merge for immediate UI; also persist via repository (API when enabled). */
  const persistMergedPreferences = useCallback(
    (partial: Partial<MapViewPreferences>): void => {
      if (!userId || !accountId) return;
      MapPreferencesCache.mergeAndSave(userId, accountId, partial);
      const merged = MapPreferencesCache.get(userId, accountId);
      if (merged) {
        void mapPreferencesRepository.save(userId, accountId, merged);
      }
    },
    [userId, accountId]
  );

  const mergeFiltersSync = useCallback(
    (filters: types.IMapFilter): void => {
      persistMergedPreferences({ filters });
    },
    [persistMergedPreferences]
  );

  const mergeTogglesSync = useCallback(
    (
      toggles: Pick<
        MapViewPreferences,
        "showSatelliteMode" | "showClusters" | "showTraffic" | "showGeozones"
      >
    ): void => {
      persistMergedPreferences(toggles);
    },
    [persistMergedPreferences]
  );

  const getCachedSync = useCallback((): MapViewPreferences | null => {
    if (!userId || !accountId) return null;
    return MapPreferencesCache.get(userId, accountId);
  }, [userId, accountId]);

  const resolvePreferences = useCallback(
    (): MapViewPreferences => resolveMapPreferences(getCachedSync()),
    [getCachedSync]
  );

  return {
    load,
    save,
    mergeFiltersSync,
    mergeTogglesSync,
    getCachedSync,
    resolvePreferences,
    canPersist,
    userId,
    accountId
  };
};

export default useMapPreferencesPersistence;
